import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api/api.service';

export type PostSaleDocumentCategory =
    | 'signed_contract'
    | 'reservation_form'
    | 'payment_check'
    | 'proof_of_payment'
    | 'replacement_check';

export type PostSalePaymentStatus =
    | 'upcoming'
    | 'due_soon'
    | 'paid'
    | 'overdue'
    | 'bounced'
    | 'replaced';

export interface PostSaleSummary {
    totalCases: number;
    documentsPending: number;
    documentsComplete: number;
    paymentScheduleActive: number;
    paymentDue: number;
    overdue: number;
    paymentCompleted: number;
    nextDueDate: string | null;
}

export interface PostSaleDocumentRecord {
    id: string;
    category: PostSaleDocumentCategory | string | null;
    fileId: string | null;
    fileName: string | null;
    uploadedByUserId: string | null;
    uploadedBy: {
        userId: string | null;
        name: string | null;
        email: string | null;
        phone: string | null;
    } | null;
    uploadedAt: string | null;
    replacesDocumentId: string | null;
    replacedByDocumentId: string | null;
    isCurrent: boolean;
    metadata: Record<string, any>;
}

export interface PostSalePaymentRecord {
    id: string;
    referenceNo: string | null;
    amount: number | null;
    dueDate: string | null;
    paymentMethod: string | null;
    paymentStatus: PostSalePaymentStatus | string | null;
    rawStatus: string | null;
    scannedCheckFileId: string | null;
    proofOfPaymentFileId: string | null;
    replacementCheckFileId: string | null;
    notes: string | null;
    paidAt: string | null;
}

export interface PostSaleCaseRecord {
    id: string;
    reservationId: string;
    orgId: string;
    operationalStatus: string;
    documentsComplete: boolean;
    nextDueDate: string | null;
    lastDueDate: string | null;
    overdueCount: number;
    dueSoonCount: number;
    paidCount: number;
    paymentCount: number;
    activePaymentCount: number;
    internalNotes: string | null;
    reservationStatus: string | null;
    reservationCreatedAt: string | null;
    property: {
        id: string | null;
        title: string | null;
        unitNumber: string | null;
        projectId: string | null;
        projectName: string | null;
    };
    investor: {
        userId: string | null;
        name: string | null;
        email: string | null;
        phone: string | null;
    };
    accountManager: {
        userId: string | null;
        email: string | null;
        phone: string | null;
    };
    documents: PostSaleDocumentRecord[];
    payments: PostSalePaymentRecord[];
}

@Injectable({ providedIn: 'root' })
export class PostSaleService {
    private api = inject(ApiService);

    getSummary(): Observable<PostSaleSummary> {
        return this.api.get<any>('/API2/post-sale/summary').pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data) => ({
                totalCases: Number(data?.totalCases ?? 0) || 0,
                documentsPending: Number(data?.documentsPending ?? 0) || 0,
                documentsComplete: Number(data?.documentsComplete ?? 0) || 0,
                paymentScheduleActive: Number(data?.paymentScheduleActive ?? 0) || 0,
                paymentDue: Number(data?.paymentDue ?? 0) || 0,
                overdue: Number(data?.overdue ?? 0) || 0,
                paymentCompleted: Number(data?.paymentCompleted ?? 0) || 0,
                nextDueDate: data?.nextDueDate ?? null
            }))
        );
    }

    listFollowUp(filter: { propertyId?: string | null; reservationId?: string | null } = {}): Observable<PostSaleCaseRecord[]> {
        let params = new HttpParams();
        if (filter.propertyId) params = params.set('property_id', String(filter.propertyId));
        if (filter.reservationId) params = params.set('reservation_id', String(filter.reservationId));
        params = params.set('per_page', '200');
        return this.api.get<any>('/API2/post-sale/follow-up', params).pipe(
            map((resp) => {
                const rows = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.DATA) ? resp.DATA : [];
                return rows.map((row: any) => this.mapCase(row));
            })
        );
    }

    getCase(reservationId: string): Observable<PostSaleCaseRecord> {
        return this.api.get<any>(`/API2/post-sale/reservations/${encodeURIComponent(reservationId)}`).pipe(
            map((resp) => this.mapCase(resp?.data ?? resp ?? {}))
        );
    }

    updateCase(reservationId: string, payload: { internal_notes?: string | null; operational_status?: string | null }): Observable<PostSaleCaseRecord> {
        return this.api.patch<any>(`/API2/post-sale/reservations/${encodeURIComponent(reservationId)}`, payload).pipe(
            map((resp) => this.mapCase(resp?.data ?? resp ?? {}))
        );
    }

    addDocument(reservationId: string, payload: {
        document_category: PostSaleDocumentCategory;
        file_id: string;
        file_name?: string | null;
        metadata?: Record<string, any>;
    }): Observable<PostSaleCaseRecord> {
        return this.api.post<any>(`/API2/post-sale/reservations/${encodeURIComponent(reservationId)}/documents`, payload).pipe(
            map((resp) => this.mapCase(resp?.data ?? resp ?? {}))
        );
    }

    replaceDocument(reservationId: string, documentId: string, payload: {
        file_id: string;
        file_name?: string | null;
        metadata?: Record<string, any>;
    }): Observable<PostSaleCaseRecord> {
        return this.api.post<any>(`/API2/post-sale/reservations/${encodeURIComponent(reservationId)}/documents/${encodeURIComponent(documentId)}/replace`, payload).pipe(
            map((resp) => this.mapCase(resp?.data ?? resp ?? {}))
        );
    }

    addPayment(reservationId: string, payload: {
        reference_no?: string | null;
        amount: number;
        due_date: string;
        payment_method?: string | null;
        payment_status?: string | null;
        scanned_check_file_id?: string | null;
        proof_of_payment_file_id?: string | null;
        replacement_check_file_id?: string | null;
        notes?: string | null;
        paid_at?: string | null;
    }): Observable<PostSaleCaseRecord> {
        return this.api.post<any>(`/API2/post-sale/reservations/${encodeURIComponent(reservationId)}/payments`, payload).pipe(
            map((resp) => this.mapCase(resp?.data ?? resp ?? {}))
        );
    }

    updatePayment(reservationId: string, paymentId: string, payload: Partial<{
        reference_no: string | null;
        amount: number | null;
        due_date: string | null;
        payment_method: string | null;
        payment_status: string | null;
        scanned_check_file_id: string | null;
        proof_of_payment_file_id: string | null;
        replacement_check_file_id: string | null;
        notes: string | null;
        paid_at: string | null;
    }>): Observable<PostSaleCaseRecord> {
        return this.api.patch<any>(`/API2/post-sale/reservations/${encodeURIComponent(reservationId)}/payments/${encodeURIComponent(paymentId)}`, payload).pipe(
            map((resp) => this.mapCase(resp?.data ?? resp ?? {}))
        );
    }

    private mapCase(data: any): PostSaleCaseRecord {
        return {
            id: String(data?.id ?? ''),
            reservationId: String(data?.reservationId ?? data?.reservation_id ?? ''),
            orgId: String(data?.orgId ?? data?.org_id ?? ''),
            operationalStatus: String(data?.operationalStatus ?? data?.operational_status ?? 'documents_pending'),
            documentsComplete: Boolean(data?.documentsComplete ?? data?.documents_complete),
            nextDueDate: data?.nextDueDate ?? data?.next_due_date ?? null,
            lastDueDate: data?.lastDueDate ?? data?.last_due_date ?? null,
            overdueCount: Number(data?.overdueCount ?? data?.overdue_count ?? 0) || 0,
            dueSoonCount: Number(data?.dueSoonCount ?? data?.due_soon_count ?? 0) || 0,
            paidCount: Number(data?.paidCount ?? data?.paid_count ?? 0) || 0,
            paymentCount: Number(data?.paymentCount ?? data?.payment_count ?? 0) || 0,
            activePaymentCount: Number(data?.activePaymentCount ?? data?.active_payment_count ?? 0) || 0,
            internalNotes: data?.internalNotes ?? data?.internal_notes ?? null,
            reservationStatus: data?.reservationStatus ?? data?.reservation_status ?? null,
            reservationCreatedAt: data?.reservationCreatedAt ?? data?.reservation_created_at ?? null,
            property: {
                id: data?.property?.id ?? null,
                title: data?.property?.title ?? null,
                unitNumber: data?.property?.unitNumber ?? data?.property?.unit_number ?? null,
                projectId: data?.property?.projectId ?? data?.property?.project_id ?? null,
                projectName: data?.property?.projectName ?? data?.property?.project_name ?? null
            },
            investor: {
                userId: data?.investor?.userId ?? data?.investor?.user_id ?? null,
                name: data?.investor?.name ?? null,
                email: data?.investor?.email ?? null,
                phone: data?.investor?.phone ?? null
            },
            accountManager: {
                userId: data?.accountManager?.userId ?? data?.accountManager?.user_id ?? null,
                email: data?.accountManager?.email ?? null,
                phone: data?.accountManager?.phone ?? null
            },
            documents: Array.isArray(data?.documents)
                ? data.documents.map((doc: any) => ({
                    id: String(doc?.id ?? ''),
                    category: doc?.category ?? doc?.document_category ?? null,
                    fileId: doc?.fileId ?? doc?.file_id ?? null,
                    fileName: doc?.fileName ?? doc?.file_name ?? null,
                    uploadedByUserId: doc?.uploadedByUserId ?? doc?.uploaded_by_user_id ?? null,
                    uploadedBy: doc?.uploadedBy ?? doc?.uploaded_by ?? null,
                    uploadedAt: doc?.uploadedAt ?? doc?.uploaded_at ?? null,
                    replacesDocumentId: doc?.replacesDocumentId ?? doc?.replaces_document_id ?? null,
                    replacedByDocumentId: doc?.replacedByDocumentId ?? doc?.replaced_by_document_id ?? null,
                    isCurrent: Boolean(doc?.isCurrent ?? doc?.is_current ?? false),
                    metadata: doc?.metadata && typeof doc.metadata === 'object' ? doc.metadata : {}
                }))
                : [],
            payments: Array.isArray(data?.payments)
                ? data.payments.map((payment: any) => ({
                    id: String(payment?.id ?? ''),
                    referenceNo: payment?.referenceNo ?? payment?.reference_no ?? null,
                    amount: payment?.amount === null || payment?.amount === undefined ? null : Number(payment.amount),
                    dueDate: payment?.dueDate ?? payment?.due_date ?? null,
                    paymentMethod: payment?.paymentMethod ?? payment?.payment_method ?? null,
                    paymentStatus: payment?.paymentStatus ?? payment?.payment_status ?? null,
                    rawStatus: payment?.rawStatus ?? payment?.raw_status ?? payment?.payment_status ?? null,
                    scannedCheckFileId: payment?.scannedCheckFileId ?? payment?.scanned_check_file_id ?? null,
                    proofOfPaymentFileId: payment?.proofOfPaymentFileId ?? payment?.proof_of_payment_file_id ?? null,
                    replacementCheckFileId: payment?.replacementCheckFileId ?? payment?.replacement_check_file_id ?? null,
                    notes: payment?.notes ?? null,
                    paidAt: payment?.paidAt ?? payment?.paid_at ?? null
                }))
                : []
        };
    }
}
